import { PurchaseResultParam } from 'config/constants/order.constants';
import { MyPurchaseQueryParams, SearchQueryParams } from 'config/types/catalog.type';
import { PurchaseResultQueryParamsType, WebPayPurchaseResultQueryParamsType } from 'config/types/order.type';
import queryString, { StringifyOptions } from 'query-string';
import React from 'react';
import { NavigateFunction, NavigateOptions, useLocation } from 'react-router';
import { RootState, store } from '../../../store';
import { AuthSlice } from '../../../store/slices/auth.slice';
import { AuthQueryParamsType, AuthType } from '../constants/auth.constants';
import { RouteName } from '../constants/route.constants';
import { MyPurchaseParams, SearchParams } from '../constants/search.constants';
import { AuthQueryParams } from '../types/auth.type';

type HistoryType = {
  navigate: NavigateFunction | null;
};

const navigateOptions: NavigateOptions = { replace: true };
const queryStringOptions: StringifyOptions = { sort: false };
const history: HistoryType = { navigate: null };

enum AddressUrlTab {
  MY_INFO = 'MY_INFO',
  MY_ADDRESS = 'MY_ADDRESS',
}

const handleQuery = (): URLSearchParams => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const AUTH_QUERY_KEYS = new Set<string>([
  AuthQueryParamsType.AUTH_TYPE,
  AuthQueryParamsType.FORGOT_PASSWORD_TOKEN,
  AuthQueryParamsType.REDIRECT_TO,
]);

const getPathnameFromPath = (path: string): string => path.split('?')[0].split('#')[0];

const isSafeInternalRedirect = (path: string): boolean => {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.includes('\\') || path.includes(':')) return false;
  const pathname = getPathnameFromPath(path);
  return Object.values(RouteName).some((route) => {
    const prefix = route.split('/:')[0];
    if (prefix === '/') return pathname === '/';
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
};

const stripAuthQueryParams = (search: string): string => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  AUTH_QUERY_KEYS.forEach((key) => params.delete(key));
  return params.toString();
};

const getLocationRedirectPath = (pathname: string, search = ''): string => {
  const remaining = stripAuthQueryParams(search);
  return remaining ? `${pathname}?${remaining}` : pathname;
};

const resolveRedirectTo = (redirectTo: string | null | undefined, urlLocation: Partial<Location>): string | null => {
  if (redirectTo) return isSafeInternalRedirect(redirectTo) ? redirectTo : null;
  const existing = new URLSearchParams(urlLocation.search ?? '').get(AuthQueryParamsType.REDIRECT_TO);
  return existing && isSafeInternalRedirect(existing) ? existing : null;
};

const handleAuthTypeAction = (
  type: AuthType,
  withAuthDrawer: boolean,
  forgotPasswordToken: string | null = null,
  urlLocation: Partial<Location>,
  redirectTo?: string | null,
): void => {
  const { navigate } = history;

  if (!navigate) return;

  const { pathname } = urlLocation;
  const storeState: RootState = store.getState();
  const { searchTerm, limit, orderBy, page, price, filteredBrands } = storeState.search;

  const authQueryParams: AuthQueryParams = { authType: type };
  if (forgotPasswordToken) {
    authQueryParams.forgotPasswordToken = forgotPasswordToken;
  }

  const resolvedRedirectTo = type === AuthType.LESS ? null : resolveRedirectTo(redirectTo, urlLocation);
  if (resolvedRedirectTo) {
    authQueryParams.redirectTo = resolvedRedirectTo;
  }

  const searchQueryParams: SearchQueryParams = { searchTerm, limit, orderBy, page };
  if (pathname === RouteName.SEARCH) {
    if (price && price.length > 0) {
      searchQueryParams.price = price;
    }
    if (filteredBrands && filteredBrands.length > 0) {
      searchQueryParams.filteredBrands = filteredBrands;
    }
  }

  const queryParams = type === AuthType.LESS ? searchQueryParams : { ...searchQueryParams, ...authQueryParams };
  const queryStringified = queryString.stringify(queryParams, queryStringOptions);
  const url =
    pathname === RouteName.SEARCH ? `${pathname}?${queryStringified}`
    : type === AuthType.LESS ? `${pathname}`
    : `${pathname}?${queryString.stringify(authQueryParams, queryStringOptions)}`;

  const actionType: AuthType = withAuthDrawer ? type : AuthType.LESS;
  store.dispatch(AuthSlice.dispatchAuthType(actionType));

  navigate(url, navigateOptions);
};

const handleSearchQueryParams = (): SearchQueryParams => {
  const query: URLSearchParams = handleQuery();
  const searchParams: SearchQueryParams = {};

  if (query.get(SearchParams.SEARCH_TERM)) {
    Object.assign(searchParams, { searchTerm: query.get(SearchParams.SEARCH_TERM) });
  }
  if (query.get(SearchParams.PAGE)) {
    Object.assign(searchParams, { page: query.get(SearchParams.PAGE) });
  }
  if (query.get(SearchParams.LIMIT)) {
    Object.assign(searchParams, { limit: query.get(SearchParams.LIMIT) });
  }
  if (query.get(SearchParams.ORDER_BY)) {
    Object.assign(searchParams, { orderBy: query.get(SearchParams.ORDER_BY) });
  }
  if (query.get(SearchParams.PRICE)) {
    Object.assign(searchParams, { price: query.get(SearchParams.PRICE) });
  }
  if (query.get(SearchParams.BRANDS)) {
    Object.assign(searchParams, { filteredBrands: query.get(SearchParams.BRANDS) });
  }

  return searchParams;
};

const handleAuthQueryParams = (): AuthQueryParams => {
  const query: URLSearchParams = handleQuery();
  const authQueryParams: AuthQueryParams = {};

  if (query.get(AuthQueryParamsType.AUTH_TYPE)) {
    Object.assign(authQueryParams, { authType: query.get(AuthQueryParamsType.AUTH_TYPE) });
  }
  if (query.get(AuthQueryParamsType.FORGOT_PASSWORD_TOKEN)) {
    Object.assign(authQueryParams, { forgotPasswordToken: query.get(AuthQueryParamsType.FORGOT_PASSWORD_TOKEN) });
  }
  if (query.get(AuthQueryParamsType.REDIRECT_TO)) {
    Object.assign(authQueryParams, { redirectTo: query.get(AuthQueryParamsType.REDIRECT_TO) });
  }

  return authQueryParams;
};

const handleSearchDispatch = (location: Partial<Location>): void => {
  const { pathname } = location;
  const {
    search: { searchTerm, page, limit, orderBy, price, filteredBrands },
  }: RootState = store.getState();

  const searchParams: SearchQueryParams = {
    searchTerm,
    page,
    limit,
    orderBy,
  };

  if (pathname === RouteName.SEARCH) {
    if (price && price.length > 0) {
      searchParams.price = price;
    }
    if (filteredBrands && filteredBrands.length > 0) {
      searchParams.filteredBrands = filteredBrands;
    }
  }

  const url: string = `${pathname}?${queryString.stringify(searchParams, queryStringOptions)}`;
  const { navigate } = history;
  navigate && navigate(url);
};

const handleMyPurchasesQueryParams = (): MyPurchaseQueryParams => {
  const query: URLSearchParams = handleQuery();
  const myPurchasequeryParams: MyPurchaseQueryParams = {};

  if (query.get(MyPurchaseParams.LIMIT)) {
    Object.assign(myPurchasequeryParams, { limit: query.get(MyPurchaseParams.LIMIT) });
  }

  if (query.get(MyPurchaseParams.ORDER_BY)) {
    Object.assign(myPurchasequeryParams, { orderBy: query.get(MyPurchaseParams.ORDER_BY) });
  }

  if (query.get(MyPurchaseParams.PAGE)) {
    Object.assign(myPurchasequeryParams, { page: query.get(MyPurchaseParams.PAGE) });
  }

  return myPurchasequeryParams;
};

const handleMyPurchasesDispatch = (location: Partial<Location>): void => {
  const { pathname } = location;
  const {
    purchase: { page, orderBy },
  }: RootState = store.getState();
  const myPurchaseParams: MyPurchaseQueryParams = { page, orderBy };
  const url: string = `${pathname}?${queryString.stringify(myPurchaseParams, queryStringOptions)}`;
  const { navigate } = history;
  navigate && navigate(url);
};

const handleWebpayPurchaseResultQueryParams = (): WebPayPurchaseResultQueryParamsType => {
  const query: URLSearchParams = handleQuery();
  const purchaseResultQueryParams: WebPayPurchaseResultQueryParamsType = {};

  if (query.get(PurchaseResultParam.TOKEN_WS)) {
    Object.assign(purchaseResultQueryParams, { token_ws: query.get(PurchaseResultParam.TOKEN_WS) });
  }

  if (query.get(PurchaseResultParam.TBK_TOKEN)) {
    Object.assign(purchaseResultQueryParams, { TBK_TOKEN: query.get(PurchaseResultParam.TBK_TOKEN) });
  }

  if (query.get(PurchaseResultParam.TBK_ORDEN_COMPRA)) {
    Object.assign(purchaseResultQueryParams, { TBK_ORDEN_COMPRA: query.get(PurchaseResultParam.TBK_ORDEN_COMPRA) });
  }

  if (query.get(PurchaseResultParam.TBK_ID_SESION)) {
    Object.assign(purchaseResultQueryParams, { TBK_ID_SESION: query.get(PurchaseResultParam.TBK_ID_SESION) });
  }

  if (query.get(PurchaseResultParam.OC)) {
    Object.assign(purchaseResultQueryParams, { oc: query.get(PurchaseResultParam.OC) });
  }

  return purchaseResultQueryParams;
};

const handlePurchaseResultQueryParams = (): PurchaseResultQueryParamsType => {
  const query: URLSearchParams = handleQuery();
  const purchaseResultQueryParams: PurchaseResultQueryParamsType = {};

  if (query.get(PurchaseResultParam.OC)) {
    Object.assign(purchaseResultQueryParams, { oc: query.get(PurchaseResultParam.OC) });
  }

  if (query.get(PurchaseResultParam.RESULT)) {
    Object.assign(purchaseResultQueryParams, { r: query.get(PurchaseResultParam.RESULT) });
  }

  return purchaseResultQueryParams;
};

const NavigationUtils = {
  AddressUrlTab,
  handleQuery,
  handleAuthTypeAction,
  history,
  handleSearchQueryParams,
  handleAuthQueryParams,
  navigateOptions,
  queryStringOptions,
  handleSearchDispatch,
  handleMyPurchasesQueryParams,
  handleMyPurchasesDispatch,
  handleWebpayPurchaseResultQueryParams,
  handlePurchaseResultQueryParams,
  isSafeInternalRedirect,
  getLocationRedirectPath,
};

export default NavigationUtils;
